/**
 * Dialog helpers from the old dialog.js, same names and signatures:
 * confirmDialog, alert, deleteDialog, openPopup, openWindow, popup_click, popup_new,
 * $_popupForm, showMessage (+ closePopup, which the popup layout called but never existed).
 *
 * Pages override `popup_click` with a global `var`, so it is always looked up on window.
 */
import { DModal, hideModal } from './Modal';
import { ajaxForm } from '../lib/http';
import { fadeOut, show } from '../lib/dom';

const callPopupClick = (opt) => (window.popup_click || popup_click)(opt);

function openerSelect(opener) {
    return document.getElementById(opener);
}

export function _popup_click(opt) {
    const select = openerSelect(opt.opener);
    if (select) {
        select.querySelectorAll('option').forEach((o) => o.remove());
        select.insertAdjacentHTML('beforeend', `<option value='${opt.value}'>${opt.text}</option>`);
    }
    hideModal(`div[data-opener='${opt.opener}']`);
}

/** Override this function for custom popup behaviour. */
export function popup_click(opt) {
    _popup_click(opt);
}

export function popup_new(opt) {
    const select = openerSelect(opt.opener);
    if (select) {
        select.querySelectorAll('option').forEach((o) => o.removeAttribute('selected'));
        select.insertAdjacentHTML('beforeend', `<option value='${opt.value}' selected>${opt.text}</option>`);
    }
    hideModal(`div[data-opener='${opt.opener}']`);
}

export function closePopup(popupid) {
    hideModal(`#${CSS.escape(popupid)}`);
}

export function $_popupForm(popupid) {
    const popup = () => document.getElementById(popupid);
    const opener = () => popup()?.dataset.opener;
    document.querySelectorAll('.form-popup').forEach((form) => {
        if (form.dataset.popupFormInit) return;
        form.dataset.popupFormInit = '1';
        ajaxForm(form, {
            beforeSubmit() {
                if (form.dataset.submitting) return false;
                form.dataset.submitting = '1';
                show('.preloader');
            },
            complete() {
                delete form.dataset.submitting;
            },
            success(cb) {
                if (cb.data) {
                    callPopupClick({ opener: opener(), value: cb.data.id, text: cb.data.text });
                } else if (cb.new) {
                    popup_new({ opener: opener(), value: cb.new.id, text: cb.new.text });
                    if (cb.message) alert(cb.message);
                } else if (cb.message) {
                    hideModal(`div[data-opener='${opener()}']`);
                    alert(cb.message);
                }
                fadeOut('.preloader');
            },
            error(xhr) {
                alert(xhr.responseJSON?.message ?? xhr.message ?? 'Terjadi kesalahan, silahkan coba lagi');
                fadeOut('.preloader');
            },
        });
    });
}

export function openPopup(opener, url, id, title) {
    if (title == null) title = '';
    if (id == null) id = 'popup' + Math.floor(Math.random() * 9867654321 * 999);
    // Pagination inside a popup re-opens into the same id: replace the old one.
    const existing = document.getElementById(id);
    if (existing) existing.remove();
    document.querySelectorAll('.modal-backdrop').forEach((b, i, all) => {
        if (existing && i === all.length - 1) b.remove();
    });

    const modl = new DModal();
    const modal = modl.createModal({
        id,
        header: title,
        headerClass: 'd-none',
        dialogClass: 'modal-lg',
        contentClass: 'fill-height-screen',
        footerClass: 'd-none',
        extra: { opener },
    });
    modl.load(url + '&popupid=' + id, () => {
        modal.querySelectorAll('ul.pagination li a').forEach((a) => {
            const href = a.getAttribute('href');
            a.setAttribute('href', `javascript:openPopup("${opener}","${href}","${id}")`);
        });
    });
    modal.addEventListener('click', (e) => {
        const picker = e.target.closest('.popup-picker');
        if (!picker || !modal.contains(picker)) return;
        callPopupClick({ opener, value: picker.dataset.value, text: picker.dataset.text });
        modl.hide();
    });
    modl.show();
    return modl;
}

export function openWindow(url, callback) {
    if (!url) return;
    const modal = document.createElement('div');
    modal.className = 'modal';
    Object.assign(modal.style, {
        display: 'block', position: 'fixed', top: '0', left: '', width: '100%', height: '100%',
        background: 'rgba(0, 0, 0, 0.5)', zIndex: '10000',
    });
    const iframe = document.createElement('iframe');
    iframe.src = url;
    iframe.frameBorder = '0';
    iframe.width = '80%';
    iframe.height = '80%';
    Object.assign(iframe.style, { position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' });
    modal.appendChild(iframe);
    const closeButton = document.createElement('button');
    closeButton.innerHTML = '<i class="fa fa-times"></i> Close';
    closeButton.className = 'btn btn-danger';
    Object.assign(closeButton.style, { position: 'absolute', top: '40px', left: '50%' });
    closeButton.onclick = () => {
        modal.remove();
        if (callback) callback();
    };
    modal.appendChild(closeButton);
    document.body.appendChild(modal);
}

export function confirmDialog(opt) {
    opt.header = 'Confirmation';
    if (opt.id == null || opt.id === '') opt.id = 'confirm-dialog';
    const modl = new DModal();
    if (opt.content.indexOf('delete') > 0) {
        opt.header = 'Delete ' + opt.header;
        opt.headerClass = 'bg-danger text-white';
    }
    if (opt.headerClass == null) opt.headerClass = 'bg-warning text-white';
    opt.dialogClass = 'modal modal-dialog-centered';
    modl.createModal(opt);
    modl.createButton('Yes', opt.yes, 'btn btn-warning text-white');
    modl.createButton('No', opt.no);
    modl.html(opt.content);
    modl.show();
    return modl;
}

export function alert(msg) {
    const modl = new DModal();
    modl.createModal({
        id: 'alert-dlg',
        headerClass: 'd-none',
        footerClass: 'd-none',
        dialogClass: 'h-100 modal-dialog-centered',
        bodyClass: 'd-flex justify-content-center',
    });
    modl.html(`<span style='font-size: 1rem;padding: 1rem;'>${msg}</span>`);
    modl.show();
    return modl;
}

export function deleteDialog(url, message) {
    const token = typeof window.csrf_token === 'function' ? window.csrf_token() : '';
    if (message == null) message = 'Do you want to delete data?';
    const form =
        `<form action='${url}' method='post' name='dlgDelete'>` +
        `<input type='hidden' name='_method' value='delete'/>` +
        `<input type='hidden' name='_token' value='${token}'/>` +
        `${message}</form>`;
    return confirmDialog({
        content: form,
        yes() {
            document.querySelector('form[name=dlgDelete]').submit();
        },
    });
}

export function showMessage(message) {
    message = String(message).toLowerCase();
    let cls = 'alert-primary';
    if (message.includes('error')) cls = 'alert-danger';
    else if (message.includes('warning')) cls = 'alert-warning';
    else if (message.includes('info')) cls = 'alert-info';
    else if (message.includes('success')) cls = 'alert-success';
    const box = document.getElementById('message-alert');
    if (!box) return;
    box.innerHTML = `<div class="alert alert-block ${cls} m-0">
            <button type="button" class="close" data-bs-dismiss="alert" aria-label="Close">
                <span aria-hidden="true">&times;</span>
            </button>
            <strong>${message}</strong>
        </div>`;
    box.style.textTransform = 'capitalize';
}
